/**
 * conversationalDiscernmentEngine.js
 *
 * Master coordinator for pastoral conversational discernment.
 * Combines all 6 discernment modules into a single compact system prompt block.
 *
 * This engine answers: "How should Kevia respond to THIS message,
 * in THIS situation, for THIS pastor?" — not generically, but wisely.
 *
 * Output: ONE compact [DISCERNIMIENTO PASTORAL] block for system prompt injection.
 * Omitted entirely when all signals are default (no unnecessary token spend).
 *
 * Design:
 *  — Consumes assessment.emotion so emotion detection runs only once
 *  — Pure function; no side effects, no localStorage, no async
 *  — Ordered priority: depth → timing → silence → question → empathy → wisdom
 */

import { resolveResponseDepth }     from "./responseDepthResolver";
import { assessPastoralTiming }     from "./pastoralTimingEngine";
import { detectSilenceAppropriate } from "./silenceAndPauseDetector";
import { buildPastoralQuestion }    from "./pastoralQuestionEngine";
import { assessEmpathyBalance }     from "./empathyBalanceEngine";
import { shapeWisdomResponse }      from "./wisdomResponseShaper";

const MAX_BLOCK_CHARS = 480;

// Depth → single-line hint (omitted for "medium" which is default)
const DEPTH_HINTS = {
  micro:      "Una frase. Solo presencia.",
  short:      "Responde brevemente. 1-2 oraciones son suficientes.",
  medium:     null,
  deep:       "Puedes desarrollar con profundidad. El tema lo requiere.",
  reflective: "Responde contemplativamente. Sin límite rígido de longitud.",
};

/**
 * Build the full discernment context for this message.
 *
 * @param {{
 *   text:               string,
 *   intent:             string,
 *   emotion:            object|null,   — from assessment.emotion (reuse, no re-detection)
 *   mood:               string,
 *   riskLevel:          string,
 *   ministryLoad:       string|null,
 *   conversationLength: number,
 * }} params
 * @returns {{
 *   discernmentBlock:  string|null,
 *   depth:             string,
 *   timing:            string,
 *   wisdomTone:        string,
 *   shouldQuestion:    boolean,
 *   questionType:      string|null,
 *   empathyBalance:    string,
 * }}
 */
export function buildDiscernmentContext({
  text,
  intent,
  emotion,
  mood,
  riskLevel          = "none",
  ministryLoad       = null,
  conversationLength = 0,
}) {
  const messageLength = text?.length ?? 0;

  const depth = resolveResponseDepth({
    intent, emotion, mood, riskLevel, messageLength, conversationLength,
    ministryLoad: ministryLoad ?? "low",
  });

  const timingResult   = assessPastoralTiming({ emotion, mood, intent, riskLevel, depth });
  const silenceResult  = detectSilenceAppropriate({ text, emotion, depth, timing: timingResult.timing });
  const questionResult = buildPastoralQuestion({ text, intent, emotion, timing: timingResult.timing, depth });
  const empathyResult  = assessEmpathyBalance({ emotion, intent, depth, riskLevel, mood });
  const wisdomResult   = shapeWisdomResponse({ emotion, mood, intent, depth, riskLevel });

  // Collect non-null instruction lines (priority order)
  const lines = [];
  if (DEPTH_HINTS[depth])              lines.push(DEPTH_HINTS[depth]);
  if (timingResult.instruction)        lines.push(timingResult.instruction);
  if (silenceResult.hint)              lines.push(silenceResult.hint);
  if (questionResult.questionHint)     lines.push(questionResult.questionHint);
  if (empathyResult.instruction)       lines.push(empathyResult.instruction);
  if (wisdomResult.instruction)        lines.push(wisdomResult.instruction);

  const discernmentBlock = lines.length > 0
    ? lines.join("\n").slice(0, MAX_BLOCK_CHARS)
    : null;

  return {
    discernmentBlock,
    depth,
    timing:         timingResult.timing,
    wisdomTone:     wisdomResult.wisdomTone,
    shouldQuestion: questionResult.shouldQuestion,
    questionType:   questionResult.questionType,
    empathyBalance: empathyResult.balance,
  };
}
